import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import SeoHead from "../../../components/seo_head";
import LGVNotificationBanner from "../../../components/lgv/lgv_notification_banner";
import PromotionActionNavigation from "../../../promotion/components/promotion_action_navigation";
import PromotionPageHeader from "../../../promotion/components/promotion_page_header";
import PromotionSectionCard from "../../../promotion/components/promotion_section_card";
import PromotionScheduleForm from "../../../promotion/components/promotion_schedule_form";
import { PROMOTION_PUBLISH_STATUS } from "../../../promotion/constants/promotion_status";
import { PROMOTION_WORKFLOW, getPromotionWorkflowState } from "../../../promotion/helpers/promotion_workflow";
import { usePromotionPosts } from "../../../promotion/hooks/use_promotion_posts";
import { usePromotionPublishing } from "../../../promotion/hooks/use_promotion_publishing";
import { usePromotionSettings } from "../../../promotion/hooks/use_promotion_settings";
import { useEventContext } from "../../../hooks/use_event_context";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const PromotionScheduling = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { eventslug } = useParams();
  const { eventId } = useEventContext({ redirectIfNotFound: false });
  const dataReady = !eventslug || Boolean(eventId);
  const promotionUrl = (page) => eventslug ? `/${eventslug}/${page}` : `/${page}`;
  const { posts, loading, refreshPosts, schedulePost, saving } = usePromotionPosts({ eventId, enabled: dataReady });
  const { publishing, publish } = usePromotionPublishing();
  const { settings } = usePromotionSettings();
  const [notification, setNotification] = useState({ show: false, type: "success", message: "" });

  useEffect(() => {
    if (!location.state?.notification) return;
    setNotification({ show: true, ...location.state.notification });
    window.history.replaceState({}, document.title);
  }, [location]);

  const readyToSchedule = posts.filter((post) => getPromotionWorkflowState(post) === PROMOTION_WORKFLOW.APPROVED);
  const scheduled = posts.filter((post) => [
    PROMOTION_PUBLISH_STATUS.SCHEDULED,
    PROMOTION_PUBLISH_STATUS.POSTING,
  ].includes(post.publishStatus));
  const published = posts.filter((post) => [PROMOTION_PUBLISH_STATUS.PUBLISHED, PROMOTION_PUBLISH_STATUS.POSTED].includes(post.publishStatus));
  const failed = posts.filter((post) => post.publishStatus === PROMOTION_PUBLISH_STATUS.FAILED);

  const publishNow = async (post) => {
    if (!window.confirm(`Publish "${post.title}" now?`)) return;
    try {
      await publish({ postId: post.id });
      await refreshPosts({ force: true });
      setNotification({ show: true, type: "success", message: "Promotion post published." });
    } catch (error) {
      await refreshPosts({ force: true });
      setNotification({ show: true, type: "error", message: error.message || "Unable to publish promotion post." });
    }
  };

  const renderReadyTable = () => (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead><tr><th>Idea</th><th>Social</th><th>Post</th><th>Schedule</th><th>Actions</th></tr></thead>
        <tbody>
          {readyToSchedule.map((post) => (
            <tr key={post.id}>
              <td>{post.ideaTitle || "Unlinked idea"}</td>
              <td><span className="badge text-bg-success text-capitalize">{post.platform || "Unknown"}</span></td>
              <td>{post.title || "Untitled"}</td>
              <td><PromotionScheduleForm post={post} onSchedule={schedulePost} onNotification={setNotification}
                disabled={saving || publishing} schedulerEnabled={settings.enabled && settings.schedulerEnabled} /></td>
              <td><div className="d-flex flex-wrap gap-2">
                <button className="btn btn-sm btn-success" disabled={publishing || !settings.enabled} onClick={() => publishNow(post)}>{publishing ? "Posting..." : "Post Immediately"}</button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(`${promotionUrl("promotion_studio")}?post=${post.id}`)}>Edit</button>
              </div></td>
            </tr>
          ))}
          {readyToSchedule.length === 0 && <tr><td colSpan="5" className="text-muted text-center py-4">No approved drafts are ready for scheduling.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  const renderTable = (rows, dateField, allowPublish) => (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead><tr><th>Idea</th><th>Social</th><th>Post</th><th>Date and Time</th>{allowPublish && <th>Action</th>}</tr></thead>
        <tbody>
          {rows.map((post) => (
            <tr key={post.id}>
              <td>{post.ideaTitle || "Unlinked idea"}</td>
              <td><span className="badge text-bg-primary text-capitalize">{post.platform || "Unknown"}</span></td>
              <td>{post.title || "Untitled"}</td>
              <td>{formatDateTime(
                dateField === "publishedAt"
                  ? post.publishedAt || post.postedAt
                  : post[dateField]
              )}{dateField === "scheduledAt" && <PromotionScheduleForm post={post} onSchedule={schedulePost} onNotification={setNotification}
                disabled={saving || publishing} schedulerEnabled={settings.enabled && settings.schedulerEnabled} />}</td>
              {allowPublish && <td><button className="btn btn-sm btn-success" disabled={publishing} onClick={() => publishNow(post)}>{publishing ? "Posting..." : "Post Immediately"}</button></td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={allowPublish ? 5 : 4} className="text-muted text-center py-4">No posts in this state.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <SeoHead title="Promotion Scheduling | Let's Go Vote" description="Scheduled and published Promotion Engine posts." noindex />
      <div className="container-fluid py-4">
        <div className="container" style={{ maxWidth: "1500px" }}>
          <PromotionPageHeader title="Scheduling" subtitle="Schedule approved drafts, publish immediately, and monitor publishing results." />
          <PromotionActionNavigation current="scheduling" promotionUrl={promotionUrl} />
          <LGVNotificationBanner {...notification} onClose={() => setNotification((current) => ({ ...current, show: false }))} />
          {loading ? <div className="alert alert-info">Loading scheduled posts...</div> : (
            <>
              <PromotionSectionCard title={`Ready to Schedule (${readyToSchedule.length})`} icon="bi bi-check-circle">{renderReadyTable()}</PromotionSectionCard>
              <PromotionSectionCard title={`Scheduled (${scheduled.length})`} icon="bi bi-calendar-event">{renderTable(scheduled, "scheduledAt", true)}</PromotionSectionCard>
              <PromotionSectionCard title={`Published (${published.length})`} icon="bi bi-send-check">{renderTable(published, "publishedAt", false)}</PromotionSectionCard>
              <PromotionSectionCard title={`Failed (${failed.length})`} icon="bi bi-exclamation-triangle">{renderTable(failed, "updatedAt", true)}</PromotionSectionCard>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PromotionScheduling;
